// Import recipes for a household from the CRM (#1562): the family card's Import recipes button opens
// #familyImportDialog (templates/users.html). Smart recipes go through `adminSmartImport` (the new path: one batch,
// one push, served from the cache when another household has the recipe); a Raw recipe is a draft the admin checks
// before it is saved. Links are read in this browser first (recipeReader.js), then by the server; a page neither can
// read asks the admin to paste it.

import { BADGE, BADGE_DOT, BUTTON } from "./ui/classes.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { escapeHTML, safeUrl } from "./familyDetailsPanel.js";
import { linksIn, readRecipeInBrowser, recipeTextFromFields, splitRecipeText } from "./recipeReader.js";

/** The most links taken at once; they go to the server 200 at a time, one batch each. */
export const MAX_LINKS = 1000;
const PER_CALL = 200;
const BROWSER_READS_AT_ONCE = 4;

let ctx = null;
let state = null;

const $ = (id) => document.getElementById(id);

/** The members a recipe can be imported for: the member documents, or the owner of a household of one. */
export function memberOptions(details, currentUid = null) {
  const members = (details.members || []).map((m) => ({ uid: m.uid, label: `${m.name || "No name"} (${m.role || "member"})` }));
  if (!members.length && details.family_id) members.push({ uid: details.family_id, label: "The household's owner" });
  const preferred = members.find((m) => m.uid === currentUid) || members[0] || null;
  return { members, selected: preferred ? preferred.uid : null };
}

const RESULT_BADGES = {
  ready: `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Added</span>`,
  queued: `<span class="${BADGE.gray}"><span aria-hidden="true" class="${BADGE_DOT.gray}"></span>Queued</span>`,
  fetch_on_device: `<span class="${BADGE.paprika}"><span aria-hidden="true" class="${BADGE_DOT.paprika}"></span>Page not read</span>`,
  failed: `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Failed</span>`
};

/** One answer of a submission as a row: what was asked, its result, and a paste box for a page nobody could read. */
export function resultRowHTML(item, result, index) {
  const url = safeUrl(item.url);
  const asked = url
    ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link} break-all">${escapeHTML(url)}</a>`
    : `<span class="text-gray-900 dark:text-white">${escapeHTML((item.text || "").slice(0, 80))}${(item.text || "").length > 80 ? "…" : ""}</span>`;
  // A link that failed can still come in as its text, with the link kept as the recipe's source (#1592).
  const pasteInstead = url
    ? `<div class="mt-2">
         <label for="familyImportPasteNew${index}" class="block text-xs/5 font-medium text-gray-700 dark:text-gray-300">Or paste the recipe's text, from wherever it is, and import that.</label>
         <textarea id="familyImportPasteNew${index}" rows="3" data-paste-new-text="${index}" class="mt-1 block w-full rounded-md bg-white px-2 py-1 text-sm/6 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-paprika-500"></textarea>
         <button type="button" data-paste-new="${index}" class="mt-2 ${BUTTON.smallPrimary}">Import this text instead</button>
       </div>`
    : "";
  const detail = result.result === "ready"
    ? `<p class="mt-1 text-sm text-gray-600 dark:text-gray-300">${escapeHTML(result.recipe_title || "")}</p>`
    : result.result === "failed"
      ? `<p class="mt-1 text-sm text-red-700 dark:text-red-400">${escapeHTML(result.failure_reason || "No reason given.")}</p>${pasteInstead}`
      : result.result === "fetch_on_device" && result.import_id
        ? `<div class="mt-2">
             <label for="familyImportPaste${index}" class="block text-xs/5 font-medium text-gray-700 dark:text-gray-300">Neither your browser nor the server could read it. Open the link, copy the recipe, paste it here.</label>
             <textarea id="familyImportPaste${index}" rows="4" data-paste-text="${escapeHTML(result.import_id)}" class="mt-1 block w-full rounded-md bg-white px-2 py-1 text-sm/6 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-paprika-500"></textarea>
             <div class="mt-2 flex gap-2">
               <button type="button" data-paste-send="${escapeHTML(result.import_id)}" data-row="${index}" class="${BUTTON.smallPrimary}">Send the recipe</button>
               <button type="button" data-paste-give-up="${escapeHTML(result.import_id)}" data-row="${index}" class="${BUTTON.small}">Can't read it either</button>
             </div>
           </div>`
        : "";
  return `
    <li class="py-3" data-result-row="${index}">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 text-sm">${asked}</div>
        <div class="flex-none">${RESULT_BADGES[result.result] || `<span class="${BADGE.gray}">${escapeHTML(result.result)}</span>`}</div>
      </div>
      ${detail}
    </li>`;
}

/** A short summary of a submission's answers. */
export function resultsSummary(results) {
  const count = (kind) => results.filter((r) => r.result === kind).length;
  const parts = [
    count("ready") && `${count("ready")} added now`,
    count("queued") && `${count("queued")} queued (about one a minute)`,
    count("fetch_on_device") && `${count("fetch_on_device")} waiting for you to paste the page`,
    count("failed") && `${count("failed")} failed`
  ].filter(Boolean);
  return parts.length ? `${parts.join(", ")}.` : "Nothing to import.";
}

function newBatchId(n) {
  return `crm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}_${n}`;
}

async function call(data) {
  const { functions, httpsCallable } = ctx;
  const res = await httpsCallable(functions, "adminSmartImport")(data);
  return res.data;
}

function setStatus(text) {
  const el = $("familyImportStatus");
  if (el) el.textContent = text;
}

function setKind(kind) {
  state.kind = kind;
  document.querySelectorAll("[data-import-kind]").forEach((btn) => btn.setAttribute("aria-pressed", String(btn.dataset.importKind === kind)));
  $("familyImportSmartPane").style.display = kind === "smart" ? "block" : "none";
  $("familyImportRawPane").style.display = kind === "raw" ? "block" : "none";
  $("familyImportKindHelp").textContent = kind === "smart"
    ? "Smart: the AI pipeline, served from the cache when another household already has the recipe."
    : "Raw: the recipe as written, no AI. You check the draft before it is saved.";
  $("familyImportNotifyHelp").textContent = kind === "smart"
    ? "One push when the last recipe is in. Leave it off to add several batches quietly."
    : "A push saying the recipe was added.";
  $("familyImportSubmit").textContent = kind === "smart" ? "Import" : "Save the recipe";
}

function setSource(source) {
  state.source = source;
  document.querySelectorAll("[data-import-source]").forEach((btn) => btn.setAttribute("aria-selected", String(btn.dataset.importSource === source)));
  $("familyImportLinksBox").style.display = source === "links" ? "block" : "none";
  $("familyImportTextBox").style.display = source === "text" ? "block" : "none";
}

function showResults(items, results) {
  state.items = items;
  state.results = results;
  $("familyImportFormView").style.display = "none";
  $("familyImportResultsView").style.display = "block";
  $("familyImportResultsSummary").textContent = resultsSummary(results);
  $("familyImportResultList").innerHTML = items.map((item, i) => resultRowHTML(item, results[i], i)).join("");
  $("familyImportSubmit").style.display = "none";
  $("familyImportCancel").textContent = "Done";
  state.changed = true;
}

/** Each link read by this browser where the site allows it, a few at a time, with the recipe as page text. */
async function readLinksInBrowser(links) {
  const items = links.map((url) => ({ url }));
  let next = 0;
  let done = 0;
  let read = 0;
  const worker = async () => {
    while (next < links.length) {
      const i = next++;
      const fields = await readRecipeInBrowser(links[i]);
      if (fields) {
        items[i] = { url: links[i], page_text: recipeTextFromFields(fields), ...(fields.title ? { title: fields.title } : {}), ...(fields.imageUrl ? { image_url: fields.imageUrl } : {}) };
        read += 1;
      }
      done += 1;
      setStatus(`Reading the pages in your browser: ${done} of ${links.length} (${read} read).`);
    }
  };
  await Promise.all(Array.from({ length: Math.min(BROWSER_READS_AT_ONCE, links.length) }, worker));
  return items;
}

async function submitSmart() {
  const userId = $("familyImportMember").value;
  const notify = $("familyImportNotify").checked;
  let items;
  if (state.source === "links") {
    const links = linksIn($("familyImportLinks").value);
    if (!links.length) return setStatus("There's no link in what you pasted.");
    if (links.length > MAX_LINKS) return setStatus(`That's ${links.length} links; send at most ${MAX_LINKS} at once.`);
    items = await readLinksInBrowser(links);
  } else {
    const text = $("familyImportText").value.trim();
    if (!text) return setStatus("Paste the recipe's text first.");
    const source = $("familyImportTextSource").value.trim();
    items = [{ text, ...(safeUrl(source) ? { source_url: source } : {}) }];
  }

  const results = [];
  for (let start = 0, n = 0; start < items.length; start += PER_CALL, n += 1) {
    setStatus(`Sending recipes ${start + 1} to ${Math.min(start + PER_CALL, items.length)} of ${items.length}…`);
    // The same id again, after a failed attempt, carries on from what that attempt made.
    state.batchIds[n] = state.batchIds[n] || newBatchId(n);
    const answer = await call({
      action: "submit",
      family_id: state.familyId,
      user_id: userId,
      client_batch_id: state.batchIds[n],
      notify,
      items: items.slice(start, start + PER_CALL)
    });
    results.push(...answer.items);
  }
  showResults(items, results);
  ctx.showToast?.(resultsSummary(results), "success");
}

/** Fills the Raw draft from fields read off a page, or from a draft split out of pasted text. */
function fillDraft(draft) {
  $("familyImportDraft").style.display = "block";
  $("familyImportDraftTitle").value = draft.title || "";
  $("familyImportDraftServings").value = draft.servings || "";
  $("familyImportDraftPrep").value = draft.prepMinutes ?? "";
  $("familyImportDraftCook").value = draft.cookMinutes ?? "";
  $("familyImportDraftIngredients").value = (draft.ingredients || []).join("\n");
  $("familyImportDraftSteps").value = (draft.instructions || []).join("\n");
  $("familyImportDraftPhoto").value = draft.imageUrl || "";
  $("familyImportDraftSource").value = draft.sourceUrl || "";
}

async function makeRawDraft() {
  const input = $("familyImportRawSource").value.trim();
  if (!input) return setStatus("Paste a link or the recipe's text first.");
  const links = linksIn(input);
  if (links.length === 1 && links[0] === input) {
    setStatus("Reading the page in your browser…");
    let fields = await readRecipeInBrowser(input);
    if (!fields) {
      setStatus("Your browser may not read that site; the server is reading it…");
      try {
        const page = await call({ action: "read_page", url: input });
        fields = {
          title: page.title,
          servings: Number.parseInt(page.servings, 10) || null,
          ingredients: page.ingredients,
          instructions: page.instructions,
          imageUrl: page.image_url,
          cookMinutes: page.cook_time_minutes
        };
      } catch (err) {
        fillDraft({ sourceUrl: input });
        return setStatus(`Neither could read it (${err.message}). Open the page and fill the draft in yourself.`);
      }
    }
    fillDraft({ ...fields, sourceUrl: input });
    return setStatus("Check the draft, then save it.");
  }
  fillDraft(splitRecipeText(input));
  setStatus("Split into a draft: check which lines are ingredients and which are steps, then save it.");
}

function lines(id) {
  return $(id).value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

async function saveRaw() {
  if ($("familyImportDraft").style.display === "none") return setStatus("Make the draft first.");
  const number = (id) => ($(id).value === "" ? null : Number($(id).value));
  const result = await call({
    action: "save_raw",
    family_id: state.familyId,
    user_id: $("familyImportMember").value,
    notify: $("familyImportNotify").checked,
    recipe: {
      title: $("familyImportDraftTitle").value.trim(),
      ingredients: lines("familyImportDraftIngredients"),
      instructions: lines("familyImportDraftSteps"),
      servings: number("familyImportDraftServings"),
      prep_time_minutes: number("familyImportDraftPrep"),
      cook_time_minutes: number("familyImportDraftCook"),
      image_url: $("familyImportDraftPhoto").value.trim() || null,
      source_url: $("familyImportDraftSource").value.trim() || null
    }
  });
  state.changed = true;
  ctx.showToast?.(`Saved '${result.recipe_title}'${result.notified ? " and told them" : ""}.`, "success");
  close();
}

async function submit() {
  const btn = $("familyImportSubmit");
  btn.disabled = true;
  try {
    if (state.kind === "smart") await submitSmart();
    else await saveRaw();
  } catch (err) {
    console.error("❌ adminSmartImport failed:", err);
    setStatus(`That didn't work: ${err.message}. Try again; recipes already made are not made twice.`);
    ctx.showToast?.(`Import failed: ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

/** A failed link's recipe as pasted text, in a batch of its own, with the link as the recipe's source (#1592). */
async function sendPastedInstead(btn) {
  const index = Number(btn.dataset.pasteNew);
  const textarea = document.querySelector(`[data-paste-new-text="${index}"]`);
  const text = textarea ? textarea.value.trim() : "";
  if (!text) return ctx.showToast?.("Paste the recipe first.", "warning");
  btn.disabled = true;
  try {
    const answer = await call({
      action: "submit",
      family_id: state.familyId,
      user_id: $("familyImportMember").value,
      client_batch_id: newBatchId(`p${index}`),
      notify: $("familyImportNotify").checked,
      items: [{ text, ...(safeUrl(state.items[index].url) ? { source_url: state.items[index].url } : {}) }]
    });
    const result = answer.items[0];
    state.items[index] = { ...state.items[index], text };
    state.results[index] = result;
    const row = document.querySelector(`[data-result-row="${index}"]`);
    if (row) row.outerHTML = resultRowHTML({ url: state.items[index].url }, result, index);
    $("familyImportResultsSummary").textContent = resultsSummary(state.results);
    state.changed = true;
  } catch (err) {
    console.error("❌ adminSmartImport submit (pasted instead) failed:", err);
    ctx.showToast?.(`Couldn't import it: ${err.message}`, "error");
    btn.disabled = false;
  }
}

async function sendPaste(btn, giveUp) {
  const importId = giveUp ? btn.dataset.pasteGiveUp : btn.dataset.pasteSend;
  const index = Number(btn.dataset.row);
  const textarea = document.querySelector(`[data-paste-text="${CSS.escape(importId)}"]`);
  const text = textarea ? textarea.value.trim() : "";
  if (!giveUp && !text) return ctx.showToast?.("Paste the recipe first.", "warning");
  btn.disabled = true;
  try {
    const result = await call(giveUp
      ? { action: "provide_text", family_id: state.familyId, import_id: importId, read_failed: true }
      : { action: "provide_text", family_id: state.familyId, import_id: importId, text });
    state.results[index] = result;
    const row = document.querySelector(`[data-result-row="${index}"]`);
    if (row) row.outerHTML = resultRowHTML(state.items[index], result, index);
    $("familyImportResultsSummary").textContent = resultsSummary(state.results);
    state.changed = true;
  } catch (err) {
    console.error("❌ adminSmartImport provide_text failed:", err);
    ctx.showToast?.(`Couldn't send it: ${err.message}`, "error");
    btn.disabled = false;
  }
}

function close() {
  hideDialog("familyImportDialog");
}

/** Binds the dialog's controls once, when the Users screen mounts. Reads nothing (platform/CLAUDE.md). */
export function bindFamilyImportDialog(context) {
  ctx = context;
  const dialog = $("familyImportDialog");
  if (!dialog) return;
  document.querySelectorAll("[data-import-kind]").forEach((btn) => btn.addEventListener("click", () => setKind(btn.dataset.importKind)));
  document.querySelectorAll("[data-import-source]").forEach((btn) => btn.addEventListener("click", () => setSource(btn.dataset.importSource)));
  $("familyImportSubmit").addEventListener("click", submit);
  $("familyImportCancel").addEventListener("click", close);
  $("familyImportRawRead").addEventListener("click", async () => {
    const btn = $("familyImportRawRead");
    btn.disabled = true;
    try {
      await makeRawDraft();
    } finally {
      btn.disabled = false;
    }
  });
  $("familyImportResultList").addEventListener("click", (e) => {
    const send = e.target.closest("[data-paste-send]");
    const giveUp = e.target.closest("[data-paste-give-up]");
    const instead = e.target.closest("[data-paste-new]");
    if (send) sendPaste(send, false);
    else if (giveUp) sendPaste(giveUp, true);
    else if (instead) sendPastedInstead(instead);
  });
  dialog.addEventListener("close", () => {
    if (state?.changed) state.onDone?.();
  });
}

/**
 * Opens the dialog for a household. `pasteImport` opens straight on one import from the Imports tab: waiting for its
 * page (`{ import_id, url }`), or failed (`{ url, failure_reason, failed: true }`), to paste its recipe instead.
 */
export function openFamilyImportDialog({ details, currentUid = null, onDone = null, pasteImport = null }) {
  if (!ctx || !$("familyImportDialog")) return;
  state = { familyId: details.family_id, kind: "smart", source: "links", batchIds: [], items: [], results: [], changed: false, onDone };
  const { members, selected } = memberOptions(details, currentUid);
  $("familyImportMember").innerHTML = members.map((m) => `<option value="${escapeHTML(m.uid)}"${m.uid === selected ? " selected" : ""}>${escapeHTML(m.label)}</option>`).join("");
  $("familyImportSubtitle").textContent = details.name ? `${details.name} · ${details.family_id}` : details.family_id;
  ["familyImportLinks", "familyImportText", "familyImportTextSource", "familyImportRawSource"].forEach((id) => { $(id).value = ""; });
  $("familyImportDraft").style.display = "none";
  $("familyImportNotify").checked = true;
  $("familyImportFormView").style.display = "block";
  $("familyImportResultsView").style.display = "none";
  $("familyImportSubmit").style.display = "";
  $("familyImportCancel").textContent = "Cancel";
  setStatus("");
  setKind("smart");
  setSource("links");
  if (pasteImport) {
    showResults([{ url: pasteImport.url }], [pasteImport.failed
      ? { import_id: pasteImport.import_id || null, result: "failed", failure_reason: pasteImport.failure_reason || "" }
      : { import_id: pasteImport.import_id, result: "fetch_on_device" }]);
  }
  showDialog("familyImportDialog");
  if (pasteImport) state.changed = false;
}
